import { DecoratorNode, $getNodeByKey, $getSelection, $isNodeSelection, $isRangeSelection, FORMAT_ELEMENT_COMMAND, COMMAND_PRIORITY_HIGH, CLICK_COMMAND, COMMAND_PRIORITY_LOW, KEY_BACKSPACE_COMMAND, KEY_DELETE_COMMAND } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { mergeRegister } from '@lexical/utils';
import { useCallback, useEffect, useMemo, useRef } from 'react';

function createUID() {
  return Math.random().toString(36).replace(/[^a-z]+/g, '').substr(0, 5);
}

function createPollOption(text = '') {
  return { text, uid: createUID(), votes: [] };
}

function cloneOption(option) {
  return { text: option.text, uid: option.uid, votes: [...option.votes] };
}

function getTotalVotes(options) {
  return options.reduce((total, o) => total + o.votes.length, 0);
}

function PollOptionComponent({ option, index, options, totalVotes, withPollNode }) {
  const checkboxRef = useRef(null);
  const votesArray = option.votes;
  const checkedIndex = votesArray.indexOf('user');
  const checked = checkedIndex >= 0;
  const votes = votesArray.length;
  const percentage = totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;

  return (
    <div className="PollNode__optionContainer">
      <div
        className={`PollNode__optionCheckboxWrapper ${checked ? 'PollNode__optionChecked' : ''}`}
        style={{ width: `${percentage}%` }}
      />
      <div className="PollNode__optionInputWrapper">
        <div className="PollNode__optionInputVotes">{votes > 0 ? `${percentage}%` : ''}</div>
        <input
          ref={checkboxRef}
          className="PollNode__optionCheckbox"
          type="checkbox"
          checked={checked}
          onChange={(e) => {
            withPollNode((node) => {
              node.toggleVote(option, 'user');
            });
          }}
        />
        <input
          className="PollNode__optionInput"
          type="text"
          value={option.text}
          placeholder={`Option ${index + 1}`}
          onChange={(e) => {
            const text = e.target.value;
            withPollNode((node) => {
              node.setOptionText(option, text);
            });
          }}
        />
      </div>
      {options.length > 2 && (
        <button
          disabled={options.length <= 2}
          className="PollNode__optionDelete"
          aria-label="Remove"
          onClick={() => {
            withPollNode((node) => {
              node.deleteOption(option);
            });
          }}
          type="button"
        >
          ×
        </button>
      )}
    </div>
  );
}

function PollComponent({ question, options, nodeKey, format }) {
  const [editor] = useLexicalComposerContext();
  const containerRef = useRef(null);
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);

  const totalVotes = useMemo(() => getTotalVotes(options), [options]);

  const withPollNode = useCallback(
    (cb) => {
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (node !== null) {
          cb(node);
        }
      });
    },
    [editor, nodeKey]
  );

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        CLICK_COMMAND,
        (event) => {
          if (containerRef.current && containerRef.current.contains(event.target)) {
            // Don't intercept clicks on interactive elements (inputs, buttons, checkboxes)
            const tag = event.target.tagName;
            if (tag === 'INPUT' || tag === 'BUTTON' || tag === 'TEXTAREA') {
              return false;
            }
            if (event.shiftKey) {
              setSelected(!isSelected);
            } else {
              clearSelection();
              setSelected(true);
            }
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        FORMAT_ELEMENT_COMMAND,
        (formatValue) => {
          const selection = $getSelection();
          if ($isNodeSelection(selection)) {
            const nodes = selection.getNodes();
            if (nodes.some((n) => n.getKey() === nodeKey)) {
              editor.update(() => {
                const node = $getNodeByKey(nodeKey);
                if (node) node.setFormat(formatValue);
              });
              return true;
            }
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        KEY_DELETE_COMMAND,
        (event) => {
          if (isSelected && $isNodeSelection($getSelection())) {
            event.preventDefault();
            const node = $getNodeByKey(nodeKey);
            if (node) { node.remove(); return true; }
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        (event) => {
          if (isSelected && $isNodeSelection($getSelection())) {
            event.preventDefault();
            const node = $getNodeByKey(nodeKey);
            if (node) { node.remove(); return true; }
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, nodeKey, isSelected, setSelected, clearSelection]);

  const alignStyle = {
    ...(format === 'center'
      ? { margin: '10px auto' }
      : format === 'right'
      ? { marginLeft: 'auto', marginRight: 0, marginTop: '10px', marginBottom: '10px' }
      : { marginLeft: 0, marginRight: 'auto', marginTop: '10px', marginBottom: '10px' }),
    ...(isSelected ? { outline: '2px solid #4766cb', outlineOffset: '2px' } : {}),
  };

  return (
    <div
      ref={containerRef}
      className={`PollNode__container${isSelected ? ' PollNode__selected' : ''}`}
      style={alignStyle}
    >
      <h2 className="PollNode__heading">{question}</h2>
      {options.map((option, index) => (
        <PollOptionComponent
          key={option.uid}
          option={option}
          index={index}
          options={options}
          totalVotes={totalVotes}
          withPollNode={withPollNode}
        />
      ))}
      <div className="PollNode__footer">
        <button
          className="PollNode__addOption"
          onClick={() => {
            withPollNode((node) => {
              node.addOption(createPollOption());
            });
          }}
          type="button"
        >
          Add Option
        </button>
      </div>
    </div>
  );
}

export class PollNode extends DecoratorNode {
  __question;
  __options;
  __format;

  static getType() {
    return 'poll';
  }

  static clone(node) {
    const cloned = new PollNode(node.__question, node.__options, node.__key);
    cloned.__format = node.__format;
    return cloned;
  }

  constructor(question, options, key) {
    super(key);
    this.__question = question;
    this.__options = options || [createPollOption(), createPollOption()];
    this.__format = '';
  }

  static importJSON(serializedNode) {
    const node = $createPollNode(serializedNode.question, serializedNode.options);
    node.__format = serializedNode.format || '';
    return node;
  }

  exportJSON() {
    return {
      type: 'poll',
      version: 1,
      question: this.__question,
      options: this.__options.map(cloneOption),
      format: this.__format,
    };
  }

  setFormat(format) {
    const self = this.getWritable();
    self.__format = format;
  }

  addOption(option) {
    const self = this.getWritable();
    self.__options = [...self.__options, option];
  }

  deleteOption(option) {
    const self = this.getWritable();
    self.__options = self.__options.filter((o) => o.uid !== option.uid);
  }

  setOptionText(option, text) {
    const self = this.getWritable();
    self.__options = self.__options.map((o) =>
      o.uid === option.uid ? { ...cloneOption(o), text } : cloneOption(o)
    );
  }

  toggleVote(option, clientID) {
    const self = this.getWritable();
    self.__options = self.__options.map((o) => {
      const newOption = cloneOption(o);
      if (o.uid === option.uid) {
        const index = newOption.votes.indexOf(clientID);
        if (index === -1) {
          newOption.votes.push(clientID);
        } else {
          newOption.votes.splice(index, 1);
        }
      }
      return newOption;
    });
  }

  exportDOM() {
    const element = document.createElement('div');
    element.className = 'poll-widget';
    element.setAttribute('data-poll-question', this.__question);
    element.setAttribute('data-poll-format', this.__format || '');
    const exportedOptions = this.__options.map((opt) => ({
      uid: opt.uid,
      text: opt.text,
      votes: opt.votes.length,
    }));
    element.setAttribute('data-poll-options', JSON.stringify(exportedOptions));
    // Alignment is applied at render time by reading data-poll-format
    return { element };
  }

  static importDOM() {
    return {
      div: (domNode) => {
        if (!domNode.classList.contains('poll-widget')) {
          return null;
        }
        return {
          conversion: (node) => {
            const question = node.getAttribute('data-poll-question') || '';
            const format = node.getAttribute('data-poll-format') || '';
            let options;
            try {
              const raw = JSON.parse(node.getAttribute('data-poll-options') || '[]');
              options = raw.map((opt) => ({
                uid: opt.uid || createUID(),
                text: opt.text || '',
                votes: Array.isArray(opt.votes) ? opt.votes : [],
              }));
            } catch (_) {
              options = [createPollOption(), createPollOption()];
            }
            const pollNode = $createPollNode(question, options);
            pollNode.__format = format;
            return { node: pollNode };
          },
          priority: 2,
        };
      },
    };
  }

  createDOM() {
    const elem = document.createElement('div');
    elem.style.display = 'block';
    return elem;
  }

  updateDOM() {
    return false;
  }

  isInline() {
    return false;
  }

  decorate() {
    return (
      <PollComponent
        question={this.__question}
        options={this.__options}
        nodeKey={this.__key}
        format={this.__format}
      />
    );
  }
}

export function $createPollNode(question, options) {
  return new PollNode(question, options);
}

export function $isPollNode(node) {
  return node instanceof PollNode;
}
